import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { usePromotionsStore } from 'src/store/benefits/promotions.store';
import * as Yup from 'yup';

const STATUS_OPTIONS = ['active', 'inactive'];
const STATUS_LABEL = ['Ativo', 'Inativo'];

const TIERS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'silver', label: 'Prata' },
  { value: 'gold', label: 'Ouro' },
  { value: 'diamond', label: 'Diamante' },
];

export default function usePromotionDetails() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const {
    getPromotionDetails,
    resetPromotionDetails,
    updatePromotion,
    createPromotion,
    isLoadingPromotionDetails,
    promotionDetails,
  } = usePromotionsStore();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');

  useEffect(() => {
    if (id) {
      getPromotionDetails(id);
    } else {
      resetPromotionDetails();
    }
  }, []);

  const defaultValues = useMemo(
    () => ({
      name: promotionDetails?.name || '',
      descriptionTitle: promotionDetails?.descriptionTitle || '',
      description: promotionDetails?.description || '',
      highlight: promotionDetails?.highlight || true,
      location: promotionDetails?.location || '',
      image: promotionDetails?.image || '',
      banners: promotionDetails?.banners || [],
      startDate: promotionDetails?.startDate ? new Date(promotionDetails.startDate) : new Date(),
      endDate: promotionDetails?.endDate ? new Date(promotionDetails.endDate) : new Date(),
      endExhibitionDate: promotionDetails?.endExhibitionDate
        ? new Date(promotionDetails.endExhibitionDate)
        : new Date(),
      info: promotionDetails?.info || '',
      silver:
        promotionDetails?.luckyNumbersByLevel.find((item) => item.level === 'silver')?.numbers || 0,
      gold:
        promotionDetails?.luckyNumbersByLevel.find((item) => item.level === 'gold')?.numbers || 0,
      diamond:
        promotionDetails?.luckyNumbersByLevel.find((item) => item.level === 'diamond')?.numbers ||
        0,
      tiers: promotionDetails?.visibleForTiers || [],
      status: promotionDetails?.status || 'active',
      priority: promotionDetails?.priority || 0,
    }),
    [promotionDetails]
  );

  const promotionSchema = Yup.object().shape({
    name: Yup.string(),
    descriptionTitle: Yup.string(),
    description: Yup.string(),
    highlight: Yup.boolean(),
    location: Yup.string(),
    image: Yup.mixed(),
    banners: Yup.mixed(),
    startDate: Yup.date().required('Data de início é obrigatória'),
    endDate: Yup.date()
      .required('Data de término é obrigatória')
      .test(
        'is-minus-than-start',
        'Data de término deve ser maior que a data de início',
        (v, context) => {
          const { startDate } = context.parent;
          const start = new Date(startDate);
          const end = new Date(v);
          return end.getTime() >= start.getTime();
        }
      ),
    endExhibitionDate: Yup.date()
      .required('Data final de exibição é obrigatória')
      .test(
        'is-minus-than-start',
        'Data final de exibição deve ser maior que a data de início',
        (v, context) => {
          const { startDate } = context.parent;
          const start = new Date(startDate);
          const end = new Date(v);
          return end.getTime() >= start.getTime();
        }
      ),
    info: Yup.string(),
    silver: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    gold: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    diamond: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(0, 'O número deve ser maior ou igual a 0!'),
    tiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    priority: Yup.number(),
  });

  const methods = useForm({
    resolver: yupResolver(promotionSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const start = watch('startDate');
  const end = watch('endDate');
  const exhibitionEnd = watch('endExhibitionDate');

  const handleDrop = useCallback(
    (acceptedFiles: any) => {
      const file = acceptedFiles[0];

      if (file) {
        setValue(
          'image',
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
    if (promotionDetails?.visibleForTiers?.length === 0 && isEdit) {
      setValue('tiers', ['all']);
    }
  }, [promotionDetails, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const luckyNumbersByLevel = [
        { level: 'silver', numbers: data.silver },
        { level: 'gold', numbers: data.gold },
        { level: 'diamond', numbers: data.diamond },
      ];

      const updatedData = {
        name: data.name,
        descriptionTitle: data.descriptionTitle,
        description: data.description,
        highlight: true,
        location: data.location,
        image: data.image,
        banners: data.banners.map((banner: any) => banner),
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        endExhibitionDate: new Date(data.endExhibitionDate),
        info: data.info,
        luckyNumbersByLevel,
        visibleForTiers:
          Array.isArray(data.tiers) && data.tiers.length === 1 && data.tiers[0] === 'all'
            ? []
            : data.tiers,
        status: data.status,
        priority: data.priority,
      };

      if (isEdit) {
        if (id) {
          await updatePromotion(id, updatedData);
          enqueueSnackbar('Sorteio atualizado com sucesso!');
          navigate(paths.dashboard.promotionsList);
        }
      } else {
        await createPromotion(updatedData);
        enqueueSnackbar('Sorteio criado com sucesso!');
        navigate(paths.dashboard.promotionsList);
      }
    } catch (error) {
      console.error(error);
    }
  });

  const handleDropMultiple = useCallback(
    (acceptedFiles: any[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const newBanners = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );

        setValue('banners', [...(values.banners || []), ...newBanners], {
          shouldValidate: true,
        });
      }
    },
    [setValue, values.banners]
  );

  const handleRemove = (fileToRemove: string) => {
    const filteredBanners = values?.banners?.filter((banner: any) => banner !== fileToRemove);
    setValue('banners', filteredBanners, { shouldValidate: true });
  };

  const handleRemoveAll = () => {
    setValue('banners', []);
  };

  useEffect(() => {
    if (Array.isArray(values.tiers) && values.tiers?.includes('all') && values.tiers.length > 1) {
      setValue('tiers', ['all']);
    }
  }, [values.tiers]);

  const isValidEndDate = useMemo(() => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return endDate.getTime() >= startDate.getTime();
  }, [end, start]);

  const isValidExhibitionEndDate = useMemo(() => {
    const startDate = new Date(start);
    const exhibitionEndDate = new Date(exhibitionEnd);
    return exhibitionEndDate.getTime() >= startDate.getTime();
  }, [exhibitionEnd, start]);

  return {
    isEdit,
    isLoadingPromotionDetails,
    methods,
    values,
    isSubmitting,
    isValidEndDate,
    onSubmit,
    handleDrop,
    handleDropMultiple,
    handleRemove,
    handleRemoveAll,
    navigate,
    enqueueSnackbar,
    control,
    STATUS_OPTIONS,
    STATUS_LABEL,
    TIERS_OPTIONS,
    isValidExhibitionEndDate,
  };
}
